/**
 * accessControl.ts — per-employee feature access (approved spec 2026-08-13).
 *
 * The DATABASE is the source of truth: migration 0151's `current_user_module_access()`
 * RPC computes the effective level per module for the signed-in person (company-off
 * wins, then super-admin, then per-person exception, then role default, else none).
 * This module only mirrors that result into the app and formats it for the UI. The
 * precedence deliberately lives in ONE place — the SQL helper — so a second copy can
 * never drift from it (the 0113 role_rank duplication is a documented wound; this
 * does not add another).
 */

import { supabase } from './supabase';
import { ModuleKey } from '../types';

/** The three levels Lis approved: No access / View only / Full access. */
export type AccessLevel = 'none' | 'view' | 'full';

/**
 * Effective level per module.
 *
 * ⚠️ AN ABSENT KEY READS AS `'full'`, NOT AS 'none'. This comment said *"Absent key = the
 * person has NO access (fail closed)"* until 2026-10-05, and the only consumer has always
 * been `ModuleContext.accessFor`, which is `accessMap?.[key] ?? 'full'` — **fail OPEN, per
 * key.** The sentence was describing an intention, in the voice of a fact, in the one file
 * a reader would check.
 *
 * It was invisible because it could not matter: the RPC emits a row for EVERY key in its
 * array, so for anything in `ACCESS_MODULE_KEYS` nothing is ever absent. It matters only
 * for a key this list carries and the function does not — which is why these two must stay
 * in step, and why `time` being in neither is the safe state rather than a gap.
 *
 * ⛔ THE BEHAVIOUR IS NOT CHANGED HERE, only the description. Which direction is right is
 * Lis's call: fail-open keeps a module reachable when the access layer cannot answer, and
 * fail-closed would have taken webmail dark for everyone the moment `mail` joined
 * `ACCESS_MODULE_KEYS` ahead of migration 0053. Corrected rather than quietly left,
 * because a wrong comment about a security default is read as the security default.
 */
export type ModuleAccessMap = Partial<Record<ModuleKey, AccessLevel>>;

/**
 * The module keys the ACCESS LAYER can answer for, in canonical order.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  ⛔ THIS IS A MIRROR OF AN ARRAY HARD-CODED IN SQL. IT IS NOT A UI CHOICE.    ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 * `public.current_user_module_access()` opens with its own `unnest(array[…])`, and that
 * array is the function's ROW GENERATOR — it must emit one row per module even when the
 * workspace has no `module_settings` row, so that "absent" is answerable as 'none' rather
 * than as nothing. This list has to match it, and the duplication is the price of that.
 *
 * ⛔ SO ADDING A KEY HERE WITHOUT WIDENING THE FUNCTION SHIPS A CONTROL THAT SAVES AND IS
 * IGNORED, which is worse than an absent one. `AccessScreen` upserts
 * `profile_module_access`, whose CHECK permits the key as soon as a migration says so — so
 * an admin would set "No access", watch it persist, and the person would keep full access,
 * because the function never joins a key outside its own array. Measured on production
 * 2026-10-05: `profile_module_access` holds ZERO rows, so this has never yet bitten anyone.
 *
 * ⚠️ AND THE COMPILER CANNOT HELP, WHICH IS WHY THIS COMMENT EXISTS. `MODULE_LABELS` below
 * is `Record<ModuleKey, string>` and the build REFUSES without an entry per key; this is
 * `ModuleKey[]`, and a short list is a perfectly valid `ModuleKey[]`. That asymmetry is
 * exactly how `time` came to be in every label map and in no list (PR #319, deliberately),
 * and how `mail` was in six maps and not here after 0052. Three instances, one cause.
 *
 * `mail` joined here with migration 0053, in the same commit that gated `/mail` in
 * `moduleRoutes` — the two are useless apart.
 *
 * ⚠️ `time` IS STILL ABSENT ON PURPOSE. It needs more than a widening: measured on
 * production 2026-10-05 11:35:58 UTC, `role_module_defaults` holds ZERO 'time' rows, so
 * adding it would resolve 'none' for every non-super-admin and take the Time & Attendance
 * widget dark. `mail` needed only the widening because 0052 seeded 8 role defaults at
 * 'full'. Guarded by `__tests__/accessModuleKeys.test.ts`.
 */
export const ACCESS_MODULE_KEYS: ModuleKey[] = [
  'hr',
  'performance',
  'projects',
  'support',
  'communication',
  'finance',
  'analytics',
  'recruitment',
  'crm',
  'marketing',
  'leave',
  'assets',
  /* ⛔ NO `mail` HERE. Webmail is an APP, not a module — Lis, 2026-09-09, restated
     2026-10-05: *"Webmail will be for all employees regardless of the feature they are
     using. Apps in general will be for all."* It was added by #567 and removed again
     here; `webmailIsAnApp.test.ts` fails if it comes back.
     ⚠️ `time` is absent for a DIFFERENT reason — it IS a module, it just has zero
     `role_module_defaults` rows, so adding it would resolve `none` for everyone. Two
     absences, two unrelated causes; do not merge them into one comment. */
];

/** Human labels for the access screen. Deliberately here, not in the SQL helper. */
export const MODULE_LABELS: Record<ModuleKey, string> = {
  hr:             'HR',
  performance:    'Performance',
  projects:       'Work',
  support:        'Support',
  communication:  'Communication',
  finance:        'Finance',
  analytics:      'Analytics',
  recruitment:    'Recruitment',
  crm:            'CRM',
  marketing:      'Marketing',
  leave:          'Leave',
  assets:         'Assets',
  time:           'Time & Attendance',
  /** The KEY is `mail` (migration 0052); the LABEL a person reads is "Webmail". */
  mail:           'Webmail',
};

/** Row shape returned by the RPC: { module_key, access_level }. */
interface AccessRow { module_key: string; access_level: string }

/**
 * Fetch the signed-in person's effective access map from the RPC.
 *
 * Returns null on ANY error (network, migration not applied, table missing). The
 * caller fails OPEN on null — exactly today's behaviour — because before migration
 * 0151 lands there IS no access layer and everyone sees what the company switched on.
 *
 * ⚠️ THE LAST LINE OF THIS BLOCK USED TO READ *"a real, successful RPC result is
 * authoritative and fail-closed (missing key = none)"*. The first half is true; **the
 * second half was not, and is corrected on the type above.** A key missing from the
 * returned map reads `'full'` at the one call site. The two statements had sat one line
 * apart — "fails OPEN on null" and "fail-closed (missing key = none)" — describing the
 * same function.
 *
 * ⛔ AND NOTE WHAT THE FILTER BELOW DOES, BECAUSE IT IS THE SAME TRAP FROM THE OTHER
 * SIDE: a row whose key is not in `ACCESS_MODULE_KEYS` is DROPPED, and a dropped key is
 * an absent key, which then reads `'full'`. So this list being short does not fail safe —
 * it fails open. That is why `mail` could not join it before migration 0053, and why the
 * two have to move together.
 */
export async function fetchModuleAccess(): Promise<ModuleAccessMap | null> {
  const { data, error } = await supabase.rpc('current_user_module_access');
  if (error || !Array.isArray(data)) return null;

  const map: ModuleAccessMap = {};
  for (const row of data as AccessRow[]) {
    const key = row.module_key as ModuleKey;
    const level = row.access_level as AccessLevel;
    if (ACCESS_MODULE_KEYS.includes(key) && (level === 'none' || level === 'view' || level === 'full')) {
      map[key] = level;
    }
  }
  return map;
}

/** A module is usable when the person's level is at least View only. */
export function hasViewAccess(level: AccessLevel | undefined): boolean {
  return level === 'view' || level === 'full';
}
